import React, { useState } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import type { ThemePreset, CustomColors } from '../contexts/ThemeContext'
import {
  PaintBrushIcon,
  SwatchIcon,
  XMarkIcon,
  ArrowPathIcon,
  CheckIcon
} from '@heroicons/react/24/outline'

interface ThemeCustomizerProps {
  isOpen: boolean
  onClose: () => void
}

const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({ isOpen, onClose }) => {
  const { theme, setTheme, resetToPreset, presets } = useTheme()
  const [activeTab, setActiveTab] = useState<'presets' | 'custom'>('presets')
  const [customColors, setCustomColors] = useState<CustomColors>(
    theme.customColors || {
      primary: '#ec4899',
      accent: '#eab308',
      primaryDark: '#be185d',
      accentDark: '#ca8a04'
    }
  )

  if (!isOpen) return null

  const handlePresetSelect = (preset: ThemePreset) => {
    resetToPreset(preset)
  }

  const handleCustomColorChange = (colorType: keyof CustomColors, value: string) => {
    const newColors = { ...customColors, [colorType]: value }
    setCustomColors(newColors)

    // Auto-generate darker shade for primary
    if (colorType === 'primary') {
      const darkerShade = darkenColor(value, 20)
      newColors.primaryDark = darkerShade
      setCustomColors(newColors)
    }

    // Auto-generate darker shade for accent
    if (colorType === 'accent') {
      const darkerShade = darkenColor(value, 15)
      newColors.accentDark = darkerShade
      setCustomColors(newColors)
    }
  }

  const applyCustomColors = () => {
    setTheme({
      ...theme,
      useCustomColors: true,
      customColors
    })
  }

  const resetCustomColors = () => {
    const presetColors = presets[theme.preset].colors
    setCustomColors(presetColors)
    setTheme({
      ...theme,
      useCustomColors: false,
      customColors: presetColors
    })
  }

  // Helper function to darken a color
  const darkenColor = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16)
    const amt = Math.round(2.55 * percent)
    const R = (num >> 16) + amt
    const G = (num >> 8 & 0x00FF) + amt
    const B = (num & 0x0000FF) + amt

    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255))
      .toString(16).slice(1).toUpperCase()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl transform transition-transform">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <PaintBrushIcon className="h-6 w-6 text-primary-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Personalizador de Temas
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('presets')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'presets'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <SwatchIcon className="h-4 w-4 inline mr-1" />
                Preestablecidos
              </button>
              <button
                onClick={() => setActiveTab('custom')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'custom'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <PaintBrushIcon className="h-4 w-4 inline mr-1" />
                Personalizado
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {activeTab === 'presets' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona uno de los temas prediseñados
                </p>
                {Object.entries(presets).map(([key, preset]) => (
                  <div
                    key={key}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      theme.preset === key && !theme.useCustomColors
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handlePresetSelect(key as ThemePreset)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{preset.name}</h3>
                      {theme.preset === key && !theme.useCustomColors && (
                        <CheckIcon className="h-5 w-5 text-primary-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{preset.description}</p>
                    <div className="flex space-x-2">
                      <div
                        className="w-8 h-8 rounded-full shadow-sm border border-gray-200"
                        style={{ backgroundColor: preset.colors.primary }}
                      />
                      <div
                        className="w-8 h-8 rounded-full shadow-sm border border-gray-200"
                        style={{ backgroundColor: preset.colors.accent }}
                      />
                      <div
                        className="w-8 h-8 rounded-full shadow-sm border border-gray-200"
                        style={{ backgroundColor: preset.colors.primaryDark }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'custom' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Personaliza los colores según tu preferencia
                  </p>
                  <button
                    onClick={resetCustomColors}
                    className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-1" />
                    Resetear
                  </button>
                </div>

                {/* Primary Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color Principal (Rosa)
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={customColors.primary}
                      onChange={(e) => handleCustomColorChange('primary', e.target.value)}
                      className="w-12 h-12 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <div className="flex-1">
                      <input
                        type="text"
                        value={customColors.primary}
                        onChange={(e) => handleCustomColorChange('primary', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                        placeholder="#ec4899"
                      />
                    </div>
                  </div>
                </div>

                {/* Primary Dark */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rosa Oscuro (Auto-generado)
                  </label>
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-12 h-12 rounded-lg border border-gray-300"
                      style={{ backgroundColor: customColors.primaryDark }}
                    />
                    <div className="flex-1">
                      <input
                        type="text"
                        value={customColors.primaryDark}
                        onChange={(e) => handleCustomColorChange('primaryDark', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* Accent Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color de Acento (Amarillo)
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={customColors.accent}
                      onChange={(e) => handleCustomColorChange('accent', e.target.value)}
                      className="w-12 h-12 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <div className="flex-1">
                      <input
                        type="text"
                        value={customColors.accent}
                        onChange={(e) => handleCustomColorChange('accent', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                        placeholder="#eab308"
                      />
                    </div>
                  </div>
                </div>

                {/* Accent Dark */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amarillo Oscuro (Auto-generado)
                  </label>
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-12 h-12 rounded-lg border border-gray-300"
                      style={{ backgroundColor: customColors.accentDark }}
                    />
                    <div className="flex-1">
                      <input
                        type="text"
                        value={customColors.accentDark}
                        onChange={(e) => handleCustomColorChange('accentDark', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Vista Previa</h4>
                  <div className="space-y-3">
                    <button
                      className="w-full px-4 py-2 rounded-md text-white font-medium hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: customColors.primary }}
                    >
                      Botón Principal
                    </button>
                    <button
                      className="w-full px-4 py-2 rounded-md text-white font-medium hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: customColors.accent }}
                    >
                      Botón de Acento
                    </button>

                    {/* Hover preview */}
                    <div className="space-y-2">
                      <p className="text-xs text-gray-600">Efecto Hover:</p>
                      <div className="border rounded p-2 hover:bg-gray-50 cursor-pointer transition-colors">
                        <span className="text-sm">Pasa el mouse aquí</span>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <div
                        className="flex-1 h-3 rounded"
                        style={{ backgroundColor: customColors.primary }}
                      />
                      <div
                        className="flex-1 h-3 rounded"
                        style={{ backgroundColor: customColors.accent }}
                      />
                      <div
                        className="flex-1 h-3 rounded"
                        style={{ backgroundColor: customColors.primaryDark }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {activeTab === 'custom' && (
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={applyCustomColors}
                className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors font-medium"
              >
                Aplicar Colores Personalizados
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ThemeCustomizer